import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { Activity, BadgeCheck, Hash, Plus, Power, Radio } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ui/use-confirm";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { cn } from "@/lib/utils";
import {
  MINI_STAT_COUNT_GRADIENT,
  MINI_STAT_LOSS_GRADIENT,
  MINI_STAT_OPEN_GRADIENT,
  MINI_STAT_WIN_GRADIENT,
} from "@/utils/miniStatGradient";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import {
  decideDeploySignal,
  getDeploySetup,
  killAllDeployments,
  listDeployments,
  listDeploySignals,
} from "../../../services/strategyDeployApi";
import type { DeployLiveMessage, DeploySetup, DeploySignal, Deployment } from "../../../types/strategyDeploy";
import { useStrategyDeployLive } from "../../../hooks/useStrategyDeployLive";
import TradeLogMiniStatistics from "../TradeLog/components/TradeLogMiniStatistics";
import { ReportsMetaIconChip } from "../Reports/components/ReportsCardChrome";
import { DeployWizard } from "./DeployWizard";
import { DeploymentCard } from "./DeploymentCard";
import { DeploymentDetail } from "./DeploymentDetail";
import { SignalCard } from "./SignalCard";
import { GREEN, MODE_BLURB, MODE_LABEL, fmtMoney, fmtR } from "./liveStrategiesFormat";
import { LiveLog } from "./LiveLog";
import { ComparePanel, familyOf } from "./ComparePanel";
import { DeployDisclaimer } from "./disclaimer";
import { ConnectTelegramButton } from "./ConnectTelegramButton";
import { LiveIconTile } from "./LiveUiParts";
import {
  MODE_ICON,
  liveBodyTextClassName,
  liveCardBodyClassName,
  liveCardClassName,
  liveOptionTitleClassName,
  livePageTitleClassName,
  liveSectionTitleClassName,
  liveSmallTextClassName,
} from "./liveStrategiesUi";

function SectionTitle({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <h2 className={liveSectionTitleClassName}>{children}</h2>
      {aside}
    </div>
  );
}

function signedGradient(v: number): string {
  return v > 0 ? MINI_STAT_WIN_GRADIENT : v < 0 ? MINI_STAT_LOSS_GRADIENT : MINI_STAT_COUNT_GRADIENT;
}

/** Kill switch + Launch a strategy: the page's actions, shown in the app's top bar. */
function LaunchpadTopBarActions({
  showKill,
  onKillAll,
  onLaunch,
}: {
  showKill: boolean;
  onKillAll: () => void;
  onLaunch: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2" data-live-top-actions="">
      {showKill ? (
        <Button
          type="button"
          variant="gradient-red"
          size="sm"
          data-font="primary"
          className="h-8 gap-1.5 px-3 shadow-none"
          onClick={onKillAll}
          data-live-kill-all=""
        >
          <Power className="size-3.5" aria-hidden />
          <span className="normal-case tracking-normal">Kill switch</span>
        </Button>
      ) : null}
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className="h-8 gap-1.5 px-3 shadow-none"
        onClick={onLaunch}
        data-live-deploy=""
      >
        <Plus className="size-3.5" aria-hidden />
        <span className="normal-case tracking-normal">Launch a strategy</span>
      </Button>
    </div>
  );
}

/** Launchpad: the user's own Lab / Pine strategies trading on paper or their broker: the signals
 *  waiting for approval, every deployment's live result, and the kill switch. */
export default function LiveStrategiesPage(): JSX.Element {
  const shadcnInset = useShadcnInsetChrome();
  // Page actions sit in the app's top bar, portalled so their state stays live.
  const { setActions: setInsetHeaderActions, mountEl: insetHeaderMount } = useShadcnInsetHeaderActions();
  useEffect(() => {
    setInsetHeaderActions(null);
  }, [setInsetHeaderActions]);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const history = useHistory();
  const location = useLocation();
  const [deps, setDeps] = useState<Deployment[] | null>(null);
  const [signals, setSignals] = useState<DeploySignal[]>([]);
  const [setup, setSetup] = useState<DeploySetup | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardHyp, setWizardHyp] = useState<string | null>(null);
  const [wizardStrategy, setWizardStrategy] = useState<{ id: string; version: string | null } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([listDeployments(), listDeploySignals(undefined)]);
      setDeps(d);
      setSignals(s);
    } catch (e) {
      setDeps((prev) => prev ?? []);
      presentMutationError(e, "Could not load Launchpad");
    }
  }, []);

  useEffect(() => {
    void load();
    getDeploySetup().then(setSetup).catch(() => setSetup(null));
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  // "Launch" from the Lab: /launchpad?deploy=<hypothesis id>; from the workspace: ?open=<deployment id>
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const id = q.get("deploy");
    const strategy = q.get("deploy_strategy");
    const openDep = q.get("open");
    if (openDep) {
      setOpenId(openDep);
      history.replace(location.pathname);
    } else if (id) {
      setWizardStrategy(null);
      setWizardHyp(id);
      setWizardOpen(true);
      history.replace(location.pathname);
    } else if (strategy) {
      // From a workspace policy of a Strategy Lab strategy: that strategy + policy version, preloaded.
      setWizardHyp(null);
      setWizardStrategy({ id: strategy, version: q.get("version") });
      setWizardOpen(true);
      history.replace(location.pathname);
    }
  }, [location.search, location.pathname, history]);

  const byId = useMemo(() => new Map((deps ?? []).map((d) => [d.id, d])), [deps]);

  useStrategyDeployLive(
    useCallback(
      (msg: DeployLiveMessage) => {
        const sig = msg.data?.signal;
        if (sig?.status === "pending" && msg.data?.event?.kind === "signal") {
          const dep = byId.get(sig.deployment_id);
          toast(`${sig.direction === "long" ? "Long" : "Short"} ${sig.symbol}: waiting for you`, {
            description: dep?.name,
          });
        }
        if (reloadTimer.current) clearTimeout(reloadTimer.current);
        reloadTimer.current = setTimeout(() => void load(), 400);
      },
      [byId, load]
    )
  );

  const decide = useCallback(
    async (signal: DeploySignal, decision: "approve" | "reject") => {
      try {
        await decideDeploySignal(signal.id, decision);
        toast.success(decision === "approve" ? "Approved. Sending the order" : "Signal rejected", {
          description: decision === "approve" ? "The fill shows up in the live log." : undefined,
        });
        await load();
      } catch (e) {
        presentMutationError(e, `Could not ${decision} the signal`);
        await load();
      }
    },
    [load]
  );

  const killAll = async () => {
    if (
      !(await confirm({
        title: "Stop every strategy?",
        description: "Every Launchpad strategy stops and all their open positions are closed at market.",
        confirmLabel: "Stop everything",
      }))
    )
      return;
    try {
      const r = await killAllDeployments();
      toast.success(`Kill switch: ${r.killed} strateg${r.killed === 1 ? "y" : "ies"} stopped`);
      await load();
    } catch (e) {
      presentMutationError(e, "Could not stop your strategies");
    }
  };

  const pending = signals.filter((s) => s.status === "pending");
  const live = (deps ?? []).filter((d) => d.status === "running");
  const totals = (deps ?? []).reduce(
    (a, d) => ({
      pnl: a.pnl + (d.summary?.pnl ?? 0),
      r: a.r + (d.summary?.r_total ?? 0),
      open: a.open + (d.summary?.open_trades ?? 0),
      closed: a.closed + (d.summary?.closed ?? 0),
    }),
    { pnl: 0, r: 0, open: 0, closed: 0 }
  );
  const hasLiveMoney = (deps ?? []).some((d) => d.mode !== "paper" && ["running", "paused"].includes(d.status));

  // One strategy running several ways at once (paper + live, versions) — compared side by side.
  const families = useMemo(() => {
    const by = new Map<string, Deployment[]>();
    for (const d of deps ?? []) {
      if (!["running", "paused"].includes(d.status)) continue;
      const k = familyOf(d);
      by.set(k, [...(by.get(k) ?? []), d]);
    }
    return Array.from(by.values()).filter((g) => g.length > 1);
  }, [deps]);
  // The board, by what matters: real money first, then paper, then what has stopped (folded).
  const groups = useMemo(() => {
    const active = (d: Deployment) => ["running", "paused", "error"].includes(d.status);
    const all = deps ?? [];
    return [
      { key: "live", title: "Live on your broker", items: all.filter((d) => active(d) && d.mode !== "paper") },
      { key: "paper", title: "Paper", items: all.filter((d) => active(d) && d.mode === "paper") },
      { key: "stopped", title: "Stopped", items: all.filter((d) => !active(d)) },
    ];
  }, [deps]);
  const [compareKey, setCompareKey] = useState<string | null>(null);
  const compareDeps = families.find((g) => familyOf(g[0]) === compareKey) ?? null;
  const plan = setup?.plan;
  const refreshSetup = () => void getDeploySetup().then(setSetup).catch(() => undefined);

  const openWizard = () => {
    setWizardHyp(null);
    setWizardStrategy(null);
    setWizardOpen(true);
  };

  const topActions = <LaunchpadTopBarActions showKill={hasLiveMoney} onKillAll={() => void killAll()} onLaunch={openWizard} />;

  const heroes = deps && deps.length
    ? [
        {
          id: "live",
          title: "Live",
          amount: String(live.length),
          gradient: MINI_STAT_COUNT_GRADIENT,
          tooltip: `Strategies running now: ${live.length} of ${deps.length} deployed`,
        },
        {
          id: "waiting",
          title: "Waiting for you",
          amount: String(pending.length),
          gradient: pending.length ? MINI_STAT_OPEN_GRADIENT : MINI_STAT_COUNT_GRADIENT,
          tooltip: "Copilot signals waiting for your approval",
        },
        {
          id: "pnl",
          title: "Closed P&L",
          amount: fmtMoney(totals.pnl),
          gradient: signedGradient(totals.pnl),
          tooltip: `Profit and loss of every closed trade, across every strategy: ${totals.closed} closed, ${totals.open} open`,
        },
        {
          id: "r",
          title: "Total R",
          amount: fmtR(totals.r),
          gradient: signedGradient(totals.r),
          tooltip: "Sum of every closed trade's result in R across every strategy (1R = the risk taken)",
        },
      ]
    : [];

  const groupHeader = (title: string, count: number, aside?: React.ReactNode) => (
    <SectionTitle
      aside={
        <span className="flex shrink-0 items-center gap-2">
          {aside}
          <ReportsMetaIconChip id={`live-group-count-${title}`} label="Strategies" value={String(count)} icon={Hash} />
        </span>
      }
    >
      {title}
    </SectionTitle>
  );

  return (
    <div
      data-live-strategies-page=""
      data-live-ui=""
      className="flex w-full min-w-0 flex-col gap-4 pb-6"
      style={shadcnInset ? undefined : { paddingTop: 40, paddingLeft: 24, paddingRight: 24 }}
    >
      {confirmDialog}
      {insetHeaderMount ? createPortal(topActions, insetHeaderMount) : null}
      {!shadcnInset ? <h1 className={livePageTitleClassName}>Launchpad</h1> : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className={cn(liveBodyTextClassName, "flex items-center gap-2")}>
            <Radio className="size-4" style={{ color: live.length ? GREEN : undefined }} aria-hidden />
            {live.length
              ? `${live.length} strateg${live.length === 1 ? "y" : "ies"} watching the market`
              : "Nothing running yet"}
          </span>
          {plan ? (
            <span className="flex items-center gap-1.5" title="What your plan allows at once" data-live-plan="">
              <ReportsMetaIconChip id="live-plan" label="Plan" value={plan.admin ? "Admin, no limits" : plan.name} icon={BadgeCheck} />
              <ReportsMetaIconChip
                id="live-plan-running"
                label="Running"
                value={plan.admin ? `${plan.usage.running} running` : `${plan.usage.running} of ${plan.running_max} running`}
                icon={Activity}
              />
              <ReportsMetaIconChip
                id="live-plan-live"
                label="Live on a broker"
                value={plan.admin ? `${plan.usage.live} live` : `${plan.usage.live} of ${plan.live_max} live`}
                icon={Radio}
              />
            </span>
          ) : null}
        </div>
        {/* Without the app's top bar (no inset chrome), the actions stay on the page. */}
        {!insetHeaderMount ? topActions : null}
      </div>

      {heroes.length ? (
        <div data-mini-stats-grid="" data-live-kpis="" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {heroes.map((h) => (
            <TradeLogMiniStatistics
              key={h.id}
              title={h.title}
              amount={h.amount}
              percentage={null}
              showPercentage={false}
              backgroundGradient={h.gradient}
              tooltip={h.tooltip}
              fitAmount
            />
          ))}
        </div>
      ) : null}

      {pending.length ? (
        <section className="flex flex-col gap-4" data-live-inbox="">
          {groupHeader("Waiting for your approval", pending.length)}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {pending.map((s) => (
              <SignalCard key={s.id} signal={s} deployment={byId.get(s.deployment_id)} onDecide={decide} />
            ))}
          </div>
        </section>
      ) : null}

      {setup && !setup.telegram_linked && (deps ?? []).some((d) => d.mode === "approve") ? (
        <div className={cn(liveCardClassName, "flex-row flex-wrap items-center justify-between gap-4 p-5")}>
          <span className="flex min-w-0 flex-col gap-1">
            <span className={liveOptionTitleClassName}>Approve signals from your phone</span>
            <span className={liveBodyTextClassName}>Connect Telegram to get Copilot signals with Approve and Reject buttons.</span>
          </span>
          <ConnectTelegramButton onReturn={refreshSetup} size="sm" />
        </div>
      ) : null}

      {deps === null ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className={cn(liveCardClassName, "h-[170px] animate-pulse bg-muted/30")} />
          ))}
        </div>
      ) : deps.length ? (
        <div className="grid min-w-0 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,460px)]">
          <div className="@container/livegroup flex min-w-0 flex-col gap-4">
            {families.length ? (
              <div className={cn(liveSmallTextClassName, "flex flex-wrap items-center gap-2")} data-live-compare-row="">
                <span>Running the same idea several ways:</span>
                {families.map((g) => (
                  <Button
                    key={familyOf(g[0])}
                    type="button"
                    size="sm"
                    variant="outline-brand"
                    data-font="primary"
                    className="h-8 px-3 shadow-none"
                    onClick={() => setCompareKey(familyOf(g[0]))}
                  >
                    <span className="normal-case tracking-normal">
                      Compare {g[0].name.split(" · ")[0]} ({g.length})
                    </span>
                  </Button>
                ))}
              </div>
            ) : null}
            {groups.map((grp) =>
              grp.items.length === 0 ? null : grp.key === "stopped" ? (
                <details key={grp.key} className={cn(liveCardClassName, liveCardBodyClassName, "group")} data-live-group={grp.key}>
                  <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                    {groupHeader(
                      grp.title,
                      grp.items.length,
                      <span className="font-[family-name:var(--font-primary)] text-sm text-[color:var(--brand-tropical-surf)]">
                        <span className="group-open:hidden">Show</span>
                        <span className="hidden group-open:inline">Hide</span>
                      </span>
                    )}
                  </summary>
                  <div className="!mt-4 grid gap-4 @[44rem]/livegroup:grid-cols-2">
                    {grp.items.map((d) => (
                      <DeploymentCard key={d.id} dep={d} onOpen={() => setOpenId(d.id)} />
                    ))}
                  </div>
                </details>
              ) : (
                // One section card per group, its strategy cards inside.
                <section key={grp.key} className={cn(liveCardClassName, liveCardBodyClassName, "min-w-0")} data-live-group={grp.key}>
                  {groupHeader(grp.title, grp.items.length)}
                  <div className="grid gap-4 @[44rem]/livegroup:grid-cols-2">
                    {grp.items.map((d) => (
                      <DeploymentCard key={d.id} dep={d} onOpen={() => setOpenId(d.id)} />
                    ))}
                  </div>
                </section>
              )
            )}
          </div>
          <LiveLog
            deployments={deps}
            className="min-w-0 xl:sticky xl:top-4"
            maxHeight={520}
            onOpenEvent={(e) => setOpenId(e.deployment_id)}
          />
        </div>
      ) : (
        <section className={cn(liveCardClassName, "items-center gap-6 px-6 py-12 text-center")} data-live-empty="">
          <div className="flex flex-col items-center gap-3">
            <LiveIconTile icon={Radio} className="size-12 [&_svg]:size-6" />
            <h2 className={cn(liveSectionTitleClassName, "text-xl")}>Put your strategy to work</h2>
            <p className={cn(liveBodyTextClassName, "m-0 max-w-[520px]")}>
              Take a strategy you built and backtested in the Strategy Lab, and run it on live prices.
              Start on paper, then trade it on your Liquid Charts or cTrader account.
            </p>
          </div>
          <div className="grid w-full max-w-[860px] gap-4 sm:grid-cols-3">
            {(["paper", "approve", "auto"] as const).map((m) => (
              <div key={m} className={cn(liveCardClassName, liveCardBodyClassName, "gap-3 text-left")}>
                <LiveIconTile icon={MODE_ICON[m]} />
                <span className={liveOptionTitleClassName}>{MODE_LABEL[m]}</span>
                <span className={liveSmallTextClassName}>{MODE_BLURB[m]}</span>
              </div>
            ))}
          </div>
          <Button type="button" variant="gradient" data-font="primary" className="h-9 px-4 shadow-none" onClick={openWizard}>
            <Plus className="size-4" aria-hidden />
            <span className="normal-case tracking-normal">Launch a strategy</span>
          </Button>
        </section>
      )}

      <Dialog open={!!compareDeps} onOpenChange={(o) => !o && setCompareKey(null)}>
        <DialogContent className="max-w-3xl sm:max-w-3xl" data-live-ui="">
          <DialogHeader>
            <DialogTitle className={liveSectionTitleClassName}>Side by side</DialogTitle>
          </DialogHeader>
          {compareDeps ? (
            <ComparePanel
              deps={compareDeps}
              onOpen={(id) => {
                setCompareKey(null);
                setOpenId(id);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <DeployDisclaimer compact />

      <DeployWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        setup={setup}
        hypothesisId={wizardHyp}
        reportStrategyId={wizardStrategy?.id ?? null}
        policyVersionId={wizardStrategy?.version ?? null}
        onRefreshSetup={refreshSetup}
        deployments={deps ?? []}
        onCreated={(dep) => {
          void load();
          refreshSetup();
          setOpenId(dep.id);
        }}
      />
      <DeploymentDetail
        deploymentId={openId}
        onOpenChange={(o) => !o && setOpenId(null)}
        onChanged={() => {
          void load();
          refreshSetup();
        }}
        onDecide={decide}
        onRunAlongside={(dep) => {
          setOpenId(null);
          setWizardHyp(dep.hypothesis_id ?? null);
          setWizardOpen(true);
        }}
      />
    </div>
  );
}
